import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "@phosphor-icons/react";
import { CALENDLY_URL } from "@/constants/site";
import { scrollToSection } from "@/components/Header";

const EASE = [0.16, 1, 0.3, 1];

const SLIDES = [
  "/hero-zen.jpg",
  "/hero-zen-2.jpg",
  "/hero-zen-3.jpg",
  "/hero-zen-4.jpg",
];
const SLIDE_MS = 6500;

const LINES = [
  { text: "Enterprise Field CTO", accent: false },
  { text: "Capabilities & GTM", accent: false },
  { text: "Execution—On Demand.", accent: true },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const reduced =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const [slide, setSlide] = useState(0);

  useEffect(() => {
    if (reduced) return undefined;
    const t = setInterval(
      () => setSlide((s) => (s + 1) % SLIDES.length),
      SLIDE_MS
    );
    return () => clearInterval(t);
  }, [reduced]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100vh] flex flex-col justify-end overflow-hidden"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <AnimatePresence>
          <motion.img
            key={slide}
            src={SLIDES[slide]}
            alt=""
            initial={{ opacity: 0, scale: 1 }}
            animate={{ opacity: 0.9, scale: 1.07 }}
            exit={{ opacity: 0 }}
            transition={{
              opacity: { duration: 1.8, ease: "easeInOut" },
              scale: { duration: 9, ease: "linear" },
            }}
            className="absolute inset-0 h-[125%] w-full object-cover object-center"
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-black/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/10 to-[#050505]/40" />
      </motion.div>

      <motion.div
        style={{ y: contentY, opacity: fade }}
        className="relative z-10 max-w-[1440px] mx-auto w-full px-6 lg:px-12 pt-40 pb-16 lg:pb-24"
      >
        <motion.p
          data-testid="hero-overline"
          initial={reduced ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.1 }}
          className="font-mono text-xs uppercase tracking-[0.3em] text-white/50 mb-8 lg:mb-12"
        >
          Tech Executive Capability Center LLC — GTM &amp; Technical Acceleration
        </motion.p>

        <h1
          data-testid="hero-headline"
          className="font-serif tracking-[-0.02em] leading-[1.12] text-4xl sm:text-5xl lg:text-6xl text-white max-w-3xl"
        >
          {LINES.map((line, i) => (
            <span key={line.text} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${line.accent ? "italic text-[#E6E4D9]" : ""}`}
                initial={reduced ? false : { y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.2, ease: EASE, delay: 0.25 + i * 0.14 }}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-subheadline"
          initial={reduced ? false : { opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: EASE, delay: 0.9 }}
          className="mt-8 lg:mt-10 max-w-2xl text-base sm:text-lg font-light leading-relaxed text-white/70"
        >
          Accelerating B2B tech platforms from 0-to-1 and $10M+ ARR without the
          sunken costs of full-time presales headcount.
        </motion.p>

        <motion.p
          data-testid="hero-posture-line"
          initial={reduced ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: EASE, delay: 1.0 }}
          className="mt-5 max-w-2xl font-mono text-[11px] sm:text-xs uppercase tracking-[0.2em] leading-relaxed text-[#E6E4D9]/80"
        >
          Light-touch, high-leverage Field CTO counsel. Architecture, sprint
          assets, and the rooms that close — not another headcount.
        </motion.p>

        <motion.div
          initial={reduced ? false : { opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: EASE, delay: 1.15 }}
          className="mt-10 lg:mt-12 flex flex-col sm:flex-row gap-4"
        >
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-primary-cta"
            className="group inline-flex items-center justify-center gap-3 bg-[#E6E4D9] text-black font-mono text-xs sm:text-sm uppercase tracking-[0.15em] px-8 py-4 hover:bg-[#C2C0B6] transition-colors duration-300"
          >
            Schedule Executive Briefing
            <ArrowUpRight
              size={16}
              weight="bold"
              className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </a>
          <a
            href="#offerings"
            data-testid="hero-secondary-cta"
            onClick={(e) => {
              e.preventDefault();
              scrollToSection("#offerings");
            }}
            className="inline-flex items-center justify-center gap-3 border border-white/25 text-white font-mono text-xs sm:text-sm uppercase tracking-[0.15em] px-8 py-4 hover:bg-white/5 hover:border-white/50 transition-colors duration-300"
          >
            Explore Offerings
          </a>
          <p
            data-testid="hero-cta-expectation"
            className="sm:self-center max-w-xs font-mono text-[10px] uppercase tracking-[0.2em] leading-relaxed text-white/40"
          >
            30-minute fit assessment — the must-win room, the technical
            objection, and the smallest engagement that creates evidence.
          </p>
        </motion.div>
      </motion.div>

      <motion.div
        initial={reduced ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.4 }}
        className="relative z-10 border-t border-white/10"
      >
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-5 flex items-center justify-between">
          <button
            data-testid="hero-scroll-indicator"
            onClick={() => scrollToSection("#why-us")}
            className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 hover:text-white/80 transition-colors duration-300"
          >
            <motion.span
              animate={reduced ? {} : { y: [0, 5, 0] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            >
              <ArrowDown size={14} />
            </motion.span>
            Scroll to explore
          </button>
          <div className="flex items-center gap-6">
            <span className="hidden sm:block font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
              Field CTO Practice — Global 2000
            </span>
            <div
              className="flex items-center gap-2"
              data-testid="hero-slide-indicators"
            >
              {SLIDES.map((_, i) => (
                <button
                  key={i}
                  data-testid={`hero-slide-dot-${i}`}
                  onClick={() => setSlide(i)}
                  aria-label={`Show scene ${i + 1}`}
                  className="py-2 flex items-center"
                >
                  <span
                    className={`block h-px transition-all duration-500 ${
                      i === slide
                        ? "w-10 bg-[#E6E4D9]"
                        : "w-5 bg-white/25 hover:bg-white/50"
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
